
import { Search } from "lucide-react";
import { useState } from "react";
import { useNavigate } from 'react-router-dom';

export interface WebsiteSubmissionFormProps {
  className?: string;
}

export const WebsiteSubmissionForm = ({ className }: WebsiteSubmissionFormProps) => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGlowing, setIsGlowing] = useState(true);
  const navigate = useNavigate();
  
  // Validate URL
  const isValidUrl = (urlString: string): boolean => {
    try {
      new URL(urlString);
      return true;
    } catch (err) {
      return false;
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate URL
    if (!websiteUrl) {
      return;
    }
    
    if (!isValidUrl(websiteUrl)) {
      return;
    }
    
    // Set loading state
    setIsSubmitting(true);
  
    // Redirect to the AgentPage with the website URL and parsed data
    navigate('/agent', { state: { websiteUrl } });
    // Clear form after successful submission
    setWebsiteUrl("");
  };

  return (
    <div className={`max-w-2xl mx-auto p-2 ${className}`}>
      <div className="mb-6 text-center">
        <div className="inline-block px-4 py-1.5 bg-brutal-charcoal text-brutal-white font-mono uppercase tracking-wider mb-3 border-4 border-brutal-black transform rotate-2 shadow-lg">
          <span role="text" aria-label="Try your Voice Assistant" className="relative font-bold">
            ✨ Try your Voice Assistant now ✨
          </span>
        </div>
      </div>

      {/* Helper text to clarify the purpose with real-time emphasis */}
      <div className="text-center mb-4">
        <p className="text-brutal-charcoal font-mono text-sm md:text-base">
          Enter your website URL below to see how your AI assistant would work in real time
        </p>
      </div>

      <form 
        onSubmit={handleSubmit} 
        className="flex relative w-full"
      >
        <div className="relative w-full">
          {/* Glow effect behind the input box */}
          {isGlowing && (
            <div className="absolute inset-0 bg-[#dda15e] blur-md opacity-50 animate-pulse"></div>
          )}
          <div className="w-full flex items-center rounded-md bg-white shadow-lg border-4 border-brutal-black overflow-hidden transition-all duration-300 relative z-10">
            <div className="pl-3 md:pl-4">
              <Search className="h-4 w-4 md:h-5 md:w-5 text-brutal-charcoal" />
            </div>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="www.yourbusiness.com"
              className="w-full pl-2 pr-2 md:pr-4 py-2 md:py-3 focus:outline-none text-brutal-charcoal font-mono text-sm md:text-lg border-none"
              aria-label="Enter your website URL"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-brutal-dark hover:bg-brutal-black transition-colors text-white font-bold py-2 px-2 md:px-4 font-mono uppercase text-xs md:text-base flex items-center justify-center min-w-[70px] md:min-w-[88px] mr-1 md:mr-4 my-1 ${isSubmitting ? 'opacity-70' : ''}`}
              aria-label="Try Spa Sense"
            >
              {isSubmitting ? "..." : "TRY NOW"}
            </button>
          </div>
        </div>
      </form>

      {/* Example text */}
      <div className="mt-2 text-center">
        <p className="text-brutal-charcoal/70 text-xs italic">
          Example: www.myspa.com or https://mybeautysalon.com
        </p>
      </div>
    </div>
  );
};

export default WebsiteSubmissionForm;
